import type { PlanGateError, PlanGateErrorCode } from '@/types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * Plan-gate UX state (MILO-280 / MILO-97).
 *
 * Holds the most recent machine-readable 403 (`{ message, code, ...meta }`) so a
 * single global modal can turn it into a targeted upgrade prompt. The 403 lives
 * here (not in component state) because any request, from anywhere, can trip a
 * gate — the api error handler fills it; PlanGateModal renders it.
 */

/** The 403 `code`s that drive the upgrade UX. Generic 403s carry no code. */
export const PLAN_GATE_CODES: PlanGateErrorCode[] = [
    'feature_not_in_plan',
    'limit_reached',
    'account_lapsed',
    // Not an upgrade prompt but the same machinery: a 403 the user can act on
    // (pick a billing company) rather than a dead end — MILO-184.
    'billing_company_required',
];

/** Narrow an arbitrary 403 body to a recognised plan-gate error. */
export function isPlanGateError(data: unknown): data is PlanGateError {
    if (!data || typeof data !== 'object') return false;
    const code = (data as { code?: unknown }).code;
    return typeof code === 'string' && (PLAN_GATE_CODES as string[]).includes(code);
}

export const usePlanGateStore = defineStore('planGate', () => {
    const error = ref<PlanGateError | null>(null);

    const isOpen = computed(() => error.value !== null);

    /** Surface a gate error in the global upgrade modal. */
    function trigger(gateError: PlanGateError): void {
        error.value = gateError;
    }

    /** Close the modal and forget the current gate error. */
    function dismiss(): void {
        error.value = null;
    }

    return { error, isOpen, trigger, dismiss };
});
